import { useMemo, useState, type ReactNode } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { placeholders } from '../../lib/skeletonUtils'
import { track } from '../../lib/analytics'
import { useTransitionNavigate } from '../../lib/viewTransition'
import { useFrozenValue } from '../../hooks/useFrozenValue'
import { useIncrementalReveal } from '../../hooks/useIncrementalReveal'
import { useArtistNextAppearances } from '../../hooks/useArtistNextAppearances'
import type { Artist, UserProfile } from '../../types'
import { EmptyState } from '../EmptyState'
import { Section } from '../Section'
import { SearchField } from '../GlobalSearch/SearchField'
import { ProfileArtistRow } from './ProfileArtistRow'
import { ProfileArtistAction } from './ProfileArtistAction'

const SEARCH_THRESHOLD = 12

const fold = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

export function ProfileArtistsView({
  artistIds,
  artists,
  myInterestedArtistIds,
  onSetArtist,
  artistUserIds = {},
  userProfiles = {},
  viewedUserId,
  loading,
  isSelf,
  empty,
}: {
  artistIds: string[]
  artists: Record<string, Artist>
  myInterestedArtistIds: Set<string>
  onSetArtist: (artistId: string, marked: boolean) => void
  artistUserIds?: Record<string, string[]>
  userProfiles?: Record<string, UserProfile>
  /** The profile's owner — left out of the friends facepile on their own rows. */
  viewedUserId?: string | null
  loading: boolean
  isSelf: boolean
  /** Replaces the default empty-state copy (e.g. a sign-up upsell for guests). */
  empty?: ReactNode
}) {
  const navigate = useTransitionNavigate()
  const { locale } = useLocaleContext()
  const [query, setQuery] = useState('')

  // Freeze membership for the visit: a removed artist stays (dimmed, undoable)
  // and following one from someone else's list doesn't reshuffle the sections.
  const ids = useFrozenValue(artistIds, !loading)
  const mineAtLoad = useFrozenValue(myInterestedArtistIds, !loading)
  const idSet = useMemo(() => new Set(ids), [ids])
  const { appearances } = useArtistNextAppearances(idSet)

  const { first, rest } = useMemo(() => {
    const q = fold(query.trim())
    const nameOf = (id: string) => artists[id]?.name ?? id
    const visible = ids.filter(id => !q || fold(nameOf(id)).includes(q))
    const byName = (a: string, b: string) => nameOf(a).localeCompare(nameOf(b))
    if (isSelf) {
      const near = visible.filter(id => appearances.get(id)?.near)
      near.sort((a, b) =>
        appearances.get(a)!.event.startDate.localeCompare(appearances.get(b)!.event.startDate),
      )
      return { first: near, rest: visible.filter(id => !appearances.get(id)?.near).sort(byName) }
    }
    return {
      first: visible.filter(id => mineAtLoad.has(id)).sort(byName),
      rest: visible.filter(id => !mineAtLoad.has(id)).sort(byName),
    }
  }, [ids, artists, query, isSelf, appearances, mineAtLoad])

  const { count, sentinelRef } = useIncrementalReveal(rest.length, { batchSize: 30, resetKey: query })

  if (loading) {
    return (
      <div>
        {placeholders(6).map((_, i) => (
          <ProfileArtistRow key={i} loading />
        ))}
      </div>
    )
  }

  if (ids.length === 0) {
    return (
      empty ?? (
        <EmptyState>
          {isSelf
            ? fbt('Heart artists on event pages to see them here', 'Empty state for the artists tab on the own profile')
            : fbt('No artist interests yet', 'Empty state for the artists tab on a public profile')}
        </EmptyState>
      )
    )
  }

  const renderRow = (artistId: string) => {
    const mine = myInterestedArtistIds.has(artistId)
    return (
      <ProfileArtistRow
        key={artistId}
        artistId={artistId}
        artist={artists[artistId]}
        appearance={appearances.get(artistId)}
        friendIds={(artistUserIds[artistId] ?? []).filter(uid => uid !== viewedUserId)}
        profiles={userProfiles}
        locale={locale}
        dimmed={isSelf && !mine}
        onOpen={() => {
          track('artist_detail_opened', { artist_id: artistId, entry_point: 'profile' })
          navigate(`/artists/${artistId}`)
        }}
        trailing={
          <ProfileArtistAction
            isSelf={isSelf}
            mine={mine}
            artistName={artists[artistId]?.name ?? ''}
            onSet={marked => onSetArtist(artistId, marked)}
          />
        }
      />
    )
  }

  const firstTitle = isSelf
    ? fbt('Playing near you soon', 'Profile artists section: your artists with an upcoming event in your area')
    : fbt('You both like', 'Profile artists section on someone else’s profile: artists both of you follow')
  const restTitle = first.length === 0
    ? undefined
    : isSelf
      ? fbt('All your artists', 'Profile artists section: the rest of your artists, A to Z')
      : fbt('Their other artists', 'Profile artists section on someone else’s profile: artists only they follow')

  const restList = (
    <div>
      {rest.slice(0, count).map(renderRow)}
      <div ref={sentinelRef} />
    </div>
  )

  return (
    <div className="space-y-4">
      {ids.length > SEARCH_THRESHOLD && (
        <SearchField
          value={query}
          onValueChange={setQuery}
          placeholder={fbt('Search artists', 'Placeholder of the search box on a profile artists tab').toString()}
        />
      )}
      {first.length > 0 && (
        <Section title={<>{firstTitle} · {first.length}</>}>
          <div>{first.map(renderRow)}</div>
        </Section>
      )}
      {rest.length > 0 && (restTitle ? (
        <Section title={<>{restTitle} · {rest.length}</>}>{restList}</Section>
      ) : restList)}
      {first.length === 0 && rest.length === 0 && (
        <EmptyState>
          {fbt('No artists match your search', 'Empty state when the profile artist search has no results')}
        </EmptyState>
      )}
    </div>
  )
}
